import { useLocation, useNavigate, useParams } from "react-router";
import { useDispatch } from "react-redux";
import {
  getDebugInfoText,
  getTitleData,
  getMdOptions,
  getMDIcon,
} from "./helpers";
import {
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { setSelectedCarouselIndex } from "redux/Modals/actions";
import {
  clearImagePageParamsContext,
  fetchClusterPageIssueTypes,
  setNavigationCluster,
  setDataPageScrollPosition,
  setClusterPageScrollPosition,
  setSimilarityData,
  setNavigationClusterLoader,
  setCardslistLoading,
  setUserTags,
} from "redux/SingleDataset/actions";

import { amplitudeTrack } from "helpers/utility/amplitude";
import {
  AMP_DATASET_EXPLORATION_EVENT__ENTITY__URL_COPIED,
  AMP_DATASET_EXPLORATION_EVENT__ENTITY__OPENED,
  AMP_DATASET_EXPLORATION_EVENT__VISUAL_SEARCH__APPLIED,
} from "helpers/constants/amplitudeEvents";
import {
  AMP_DATASET_EXPLORATION_PROPERTY__CLUSTER_SIZE,
  AMP_DATASET_EXPLORATION_PROPERTY__CONTEXT,
  AMP_DATASET_EXPLORATION_PROPERTY__TARGET,
} from "helpers/constants/amplitudeProperties";

import {
  addQueryParameter,
  copyToClipboard,
  getInitialMDType,
  getMediaDimensions,
  useWindowResize,
} from "helpers/utility/utilities";
import { DATASET_CREATION_PAGE, ENRICH_PAGE } from "helpers/constants/pages";
import {
  OBJECT_ID_QUERY_PARAMETER,
  PAGE_PARAMETER,
  DATA_PAGE_PARAMETER,
  ENTITY_TYPE_PARAMETER,
  LOADING_OR_ERROR_CARD_DIMENSION_NUMBER,
  USER_TAGS_PARAMETER,
  LABELS_PARAMETER,
  REVIEW_PARAMETER,
} from "helpers/constants/miscellaneous";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import {
  CardBodyImage,
  CardTypes,
  CardVariant,
  DataPreview,
  EventTarget,
  FilterOption,
  HandleSelect,
  isValidEntityType,
  MetadataType,
  QueryOperator,
  RelevanceType,
  SetStateFunction,
  VqlQueryBase,
  LabelsFilter,
  TagsFilter,
  SingleExportItem,
} from "types";
import { AnnotatedBoundingBox } from "extractedBackendTypes";

import AssetPreviewCard from "views/components/AssetPreviewCard";
import FindSimilarIcon from "assets/icons/FindSimilarIcon";
import CheckIcon from "assets/icons/CheckIcon";
import LinkIcon from "assets/icons/LinkIcon";
import XIcon from "assets/icons/XIcon";
import CardHeader from "./CardHeader";
import CardBody from "./CardBody";

import styles from "./style.module.scss";
import classNames from "classnames";
import useQueryParam from "hooks/useQueryParam";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import {
  createAndUpdateLabelsFilter,
  createAndUpdateUserTagsFilter,
} from "helpers/utility/vqlHelpers";
import useVqlParams from "hooks/useVqlParams";
import { isVqlQuery } from "helpers/utility/vqlHelpers";
import ImageIcon from "assets/icons/imageIcon";
import { ActionItem, Cluster } from "../index";
import { FloatButton } from "antd";
import SelectList from "views/uikit/FormFields/SelectList";
import VLPopover from "views/uikit/VLPopover";
import VLFloatButton from "views/uikit/Buttons/VLFloatButton";

export interface EnrichmentCardProps {
  page: string;
  index: number;
  singleData: any;
  userTagsData: any;
  datasetId: string;
  clusterID: string;
  isVertex: boolean;
  isDataPage: boolean;
  cardElementRef: any;
  isClusterPage: boolean;
  isVertexCard?: boolean;
  indexOfPointerOut: number;
  showSelectOption?: boolean;
  showMetadataSummary: boolean;
  modelType?: "IMAGE" | "OBJECT";
  globalMDType: MetadataType | null;
  containerRef: RefObject<HTMLDivElement>;

  clusters?: any[];
  showDebugInfo?: boolean;
  isNewFiltersPanel?: boolean;
  isCardslistLoading?: boolean;
  datasetLabels?: FilterOption[];
  exportData?: SingleExportItem[];
  isAssetPreviewEnabled?: boolean;
  datasetUserTags?: FilterOption[];
  relevanceScores?: number[] | null;
  isNavigationClusterLoading?: boolean;

  onSelect: HandleSelect;
  onRemoveVertex?: (e: MouseEvent) => void;
  onIndexOfPointerOut: SetStateFunction<number>;
  onGlobalMDTypeChange: (type: MetadataType) => void;
  onSetVertex?: (id: string, type: "MEDIA" | "CLUSTER") => void;
  cardTitle?: string;
}

const EnrichmentCard = ({
  index,
  page,
  isVertex,
  clusterID,
  modelType,
  isDataPage,
  singleData,
  userTagsData,
  isVertexCard,
  containerRef,
  globalMDType,
  isClusterPage,
  cardElementRef,
  showSelectOption,
  indexOfPointerOut,
  showMetadataSummary,

  clusters = [],
  exportData = [],
  datasetLabels = [],
  datasetUserTags = [],
  showDebugInfo = false,
  relevanceScores = null,
  isNewFiltersPanel = false,
  isCardslistLoading = false,
  isAssetPreviewEnabled = false,
  isNavigationClusterLoading = false,

  onSelect,
  onSetVertex,
  onRemoveVertex,
  onIndexOfPointerOut,
  onGlobalMDTypeChange,
  cardTitle,
}: EnrichmentCardProps) => {
  const [isHovering, setIsHovering] = useState<any>(false);
  const [mdLabels, setMdLabels] = useState<string[] | null>(null);
  const { addVqlQuery, vqlQueries, updateVqlQuery, removeVqlQuery } =
    useVqlParams();

  const entitySearchParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityParams = isValidEntityType(entitySearchParam)
    ? entitySearchParam
    : null;

  const queryParamsFilters = useAllQueryParamsFilters();
  const [linkClicked, setLinkClicked] = useState(false);
  const [hoveredAssetData, setHoveredAssetData] = useState({
    id: "",
    dimensions: { naturalHeight: 0, naturalWidth: 0 },
    error: false,
  });
  const { datasetId, clusterId, enrichDatasetId, newDatasetId } = useParams();
  const assetPreviewFlagRef = useRef(true);
  const { width } = useWindowResize();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const uniqueClusterWrapperId = `clusterCardWrapper-${index}`;

  const isDataCreationPage = page === DATASET_CREATION_PAGE;
  const isEnrichmentPage = page === ENRICH_PAGE;

  const isObjectModel = modelType === "OBJECT";
  const bodyEntityType: CardTypes = singleData.type;
  const isObjectCard = bodyEntityType === "OBJECT";
  const inReview = searchParams.has(REVIEW_PARAMETER);

  const hideMetadataOptions = isEnrichmentPage || isDataCreationPage;
  const showActionButtons = isHovering && !hideMetadataOptions;

  const isBodyUnclickable = !!enrichDatasetId || isVertex || !!newDatasetId;

  const isSingleEntityOnDataPage =
    isDataPage && singleData.previews.length === 1;

  const fileName = hideMetadataOptions
    ? singleData.file_name || singleData.original_uri
    : isSingleEntityOnDataPage
      ? singleData.previews[0].video_uri
        ? singleData.previews[0].video_uri.split("/")[
            singleData.previews[0].video_uri.split("/").length - 1
          ]
        : singleData.previews[0].file_name
      : isClusterPage
        ? singleData.video_uri
          ? singleData.video_uri.split("/")[
              singleData.video_uri.split("/").length - 1
            ]
          : singleData.file_name
        : "";

  const formattedFilename = decodeURIComponent(fileName);

  const getEnrichPageLabels = (): string[] => {
    if (isObjectCard) {
      switch (globalMDType) {
        case MetadataType.IMAGE_LABELS:
        case MetadataType.OBJECT_LABELS:
        case MetadataType.USER_TAGS:
          return singleData.labels || [];
        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.IMAGE_CAPTIONS:
          return singleData.caption ? [singleData.caption] : [];
        default:
          return [];
      }
    } else {
      switch (globalMDType) {
        case MetadataType.IMAGE_LABELS:
        case MetadataType.USER_TAGS:
          return singleData.labels || [];

        case MetadataType.OBJECT_LABELS:
          return singleData.objects
            .map((obj: any) => obj.labels?.[0])
            .filter((label: any): label is string => !!label);

        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.IMAGE_CAPTIONS:
          return isObjectModel
            ? singleData.objects
                .map((obj: any) => obj.caption)
                .filter((caption: any): caption is string => !!caption)
            : singleData.caption
              ? [singleData.caption]
              : [];

        default:
          return [];
      }
    }
  };

  const getCreationPageLabels = (): string[] => {
    const preview: DataPreview = singleData;
    let objectAnnotations = preview.object_annotations || [];
    let imageAnnotations = preview.image_annotations || [];

    if (!!objectAnnotations.length) {
      return objectAnnotations
        .map((singleAnnotation) =>
          singleAnnotation.annotations ? singleAnnotation.annotations[0] : null
        )
        .filter((item): item is string => typeof item === "string");
    }

    return imageAnnotations;
  };

  const getCreationBoundingBoxes = (): number[][] => {
    let objectAnnotations: AnnotatedBoundingBox[] =
      singleData.object_annotations || [];

    if (!!objectAnnotations.length) {
      return objectAnnotations
        .map((annotation) => {
          let { col_x, row_y, width, height } = annotation;
          if (col_x && row_y && width && height) {
            return [col_x, row_y, width, height];
          } else return [];
        })
        .slice(0, 5);
    } else {
      return [];
    }
  };

  const getEnrichmentBoundingBoxes = (): number[][] => {
    if (isObjectCard) {
      switch (globalMDType) {
        case MetadataType.IMAGE_LABELS:
        case MetadataType.USER_TAGS:
          return [];

        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.OBJECT_LABELS:
        case MetadataType.IMAGE_CAPTIONS:
          return singleData.bounding_box ? [singleData.bounding_box] : [];
        default:
          return [];
      }
    } else {
      switch (globalMDType) {
        case MetadataType.IMAGE_LABELS:
        case MetadataType.USER_TAGS:
          return [];

        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.OBJECT_LABELS:
        case MetadataType.IMAGE_CAPTIONS:
          return singleData.objects
            .map((obj: any) => obj.bounding_box)
            .filter((label: any): label is number[] => !!label)
            .slice(0, 5);

        default:
          return [];
      }
    }
  };

  const getDataStrings = (
    data: any,
    userTagsData: any
  ): {
    labels: string[];
    userTags: string[];
    captions: string[];
  } => {
    if (hideMetadataOptions) {
      return { labels: [], userTags: [], captions: [] };
    }

    if (hoveredAssetData.id) {
      const assetData = getAssetData();
      return {
        labels: assetData.labels || [],
        userTags: assetData.user_tags || [],
        captions: assetData.caption ? [assetData.caption] : [],
      };
    }

    const labels =
      isDataPage && data.labels
        ? Object.keys(data.labels)
        : isClusterPage
          ? data.labels || []
          : [];

    const userTags = isDataPage
      ? Object.keys(userTagsData)
      : isClusterPage
        ? userTagsData || []
        : [];

    const captions = isDataPage
      ? data.captions || []
      : data.caption
        ? [data.caption]
        : [];

    return { labels, userTags, captions };
  };

  const { labels, userTags, captions } = useMemo(
    () => getDataStrings(singleData, userTagsData),
    //eslint-disable-next-line
    [singleData, hoveredAssetData, userTagsData]
  );
  const mdType = useMemo(
    () =>
      getInitialMDType(
        globalMDType,
        userTags,
        labels,
        captions,
        entityParams,
        hideMetadataOptions
      ),
    //eslint-disable-next-line
    [globalMDType]
  );
  const [cardMDType, setCardMDType] = useState<MetadataType>(mdType);

  //using a specific index for cart selections
  const specificIndex = `id-${
    singleData.cluster_id ? singleData.cluster_id : singleData.media_id
  }-index-${index}`;

  const isObjectOnEnrichmentPage = isEnrichmentPage && modelType === "OBJECT";
  const isSingleObject = isDataPage
    ? false
    : isObjectOnEnrichmentPage ||
      singleData.type === "OBJECT" ||
      globalMDType === MetadataType.OBJECT_LABELS;

  const boundingBox = isDataCreationPage
    ? getCreationBoundingBoxes()
    : isObjectOnEnrichmentPage
      ? getEnrichmentBoundingBoxes()
      : isSingleObject
        ? [singleData.bounding_box]
        : null;

  const videoURIForCluster = isClusterPage && singleData.video_uri;
  const videoURIsForData =
    isDataPage &&
    singleData.previews.map((preview: any) => preview.video_uri || "");

  const entityParam = searchParams.has(ENTITY_TYPE_PARAMETER)
    ? `?${ENTITY_TYPE_PARAMETER}=${searchParams.get(ENTITY_TYPE_PARAMETER)}`
    : "";

  const dynamicAssetPreviewEnabled =
    (indexOfPointerOut === -1 || indexOfPointerOut === index) &&
    isAssetPreviewEnabled;

  const hoverDelay = dynamicAssetPreviewEnabled ? 700 : 0;

  const endContentLoading = () => {
    if (index === 0 && isVertexCard) {
      return;
    }
    if (isNavigationClusterLoading || isCardslistLoading) {
      dispatch(setNavigationClusterLoader(false));
      dispatch(setCardslistLoading(false));
    }
  };

  const isSelected = useMemo(() => {
    if (isDataPage) {
      return !!exportData.find(
        (singleExp: any) => singleExp.cluster_id === singleData.cluster_id
      );
    } else if (isClusterPage) {
      const selectedParentCluster = exportData.find(
        (data: any) =>
          data.cluster_id === clusterID && data.exportType === "cluster"
      );
      if (selectedParentCluster) {
        //check if the media is selected to be removed from cluster selection
        const selectedForRemoval = selectedParentCluster.excludeMediaIDs
          ? selectedParentCluster.excludeMediaIDs.includes(singleData.media_id)
          : false;
        if (selectedForRemoval) {
          return false;
        }
        return true;
      } else {
        return !!exportData.find(
          (data: any) => data.media_id === singleData.media_id
        );
      }
    }
    return false;
    //eslint-disable-next-line
  }, [exportData, isDataPage, isClusterPage]);

  const cardVariant: CardVariant = isVertex
    ? CardVariant.VERTEX
    : isSelected
      ? CardVariant.SELECTED
      : CardVariant.DEFAULT;

  let finalTitle: string | JSX.Element;
  let titleIcon: JSX.Element;

  if (cardTitle) {
    finalTitle = cardTitle;
    titleIcon = <ImageIcon />;
  } else {
    const titleData = getTitleData(
      singleData,
      clusterId,
      datasetId,
      formattedFilename,
      isSelected,
      isVertex
    );
    finalTitle = titleData.title;
    titleIcon = titleData.titleIcon;
  }

  const cardClassNames = classNames(styles.dataCard, {
    [styles.dataCardWithOutline]: isHovering,
    [styles.dataCardForSimilarity]: isVertex,
    [styles.dataCardForExport]: showSelectOption && isSelected && !isVertex,
  });

  const getRelevanceType = (relevance: number) => {
    let relevanceType: RelevanceType | null = null;

    if (!relevanceScores) {
      return null;
    }

    if (relevanceScores.length === 3) {
      const H = relevanceScores[0];
      const M = relevanceScores[1];
      const L = relevanceScores[2];

      if (relevance >= 0 && relevance <= H) {
        relevanceType = "High";
      } else if (relevance > H && relevance <= M) {
        relevanceType = "Medium";
      } else if (relevance > M && relevance <= L) {
        relevanceType = "Low";
      }
    }
    return relevanceType;
  };

  const getDynamicImages = (type: "plain" | "thumb", previews: any) => {
    let result: CardBodyImage[] = [];
    previews = previews?.length ? previews : [];

    if (type === "thumb") {
      result = previews.map((preview: any) => {
        const relevanceType = getRelevanceType(preview.relevance_score || -1);
        return {
          src: preview.media_thumb_uri,
          id: preview.media_id,
          relevanceType,
        };
      });
    } else if (type === "plain") {
      result = previews.map((preview: any) => {
        const relevanceType = getRelevanceType(preview.relevance_score || -1);
        return {
          src: preview.media_uri,
          id: preview.media_id,
          relevanceType,
        };
      });
    }

    return result;
  };

  const plainImages = getDynamicImages("plain", singleData.previews);
  const thumbImages = getDynamicImages("thumb", singleData.previews);

  const relevanceType = getRelevanceType(singleData?.relevance_score || -1);
  const plainImage: CardBodyImage = {
    id: singleData.media_id,
    src: singleData.media_uri,
    relevanceType,
  };
  const thumbImage: CardBodyImage = {
    id: singleData.media_id,
    src: singleData.media_thumb_uri,
    relevanceType,
  };

  function getAssetData() {
    let assetData;

    if (hoveredAssetData.id) {
      if (isDataPage) {
        assetData = singleData.previews.find(
          (preview: any) => preview.media_id === hoveredAssetData.id
        );
      } else if (isClusterPage) {
        assetData =
          singleData.media_id === hoveredAssetData.id ? singleData : undefined;
      }
    }

    return assetData ?? {};
  }

  const setAssetPreviewFlag = (value: boolean) => {
    assetPreviewFlagRef.current = value;
  };

  const clearAssetData = () => {
    setHoveredAssetData({
      id: "",
      dimensions: { naturalHeight: 0, naturalWidth: 0 },
      error: false,
    });
  };

  const updateAssetData = async (id: string, source: string) => {
    const dimensions = {
      naturalWidth: LOADING_OR_ERROR_CARD_DIMENSION_NUMBER,
      naturalHeight: LOADING_OR_ERROR_CARD_DIMENSION_NUMBER,
    };

    try {
      const res = await getMediaDimensions(source);
      const result = { id, dimensions: res, error: false };

      if (assetPreviewFlagRef.current) {
        setHoveredAssetData((prev) => {
          const oldHeight = prev.dimensions.naturalHeight;
          const oldWidth = prev.dimensions.naturalWidth;

          const newHeight = res.naturalHeight;
          const newWidth = res.naturalWidth;

          // Normalize dimensions by the larger dimension to ensure consistent scale
          const normalizedOldHeight = oldHeight / Math.max(oldHeight, oldWidth);
          const normalizedOldWidth = oldWidth / Math.max(oldHeight, oldWidth);
          const normalizedNewHeight = newHeight / Math.max(newHeight, newWidth);
          const normalizedNewWidth = newWidth / Math.max(newHeight, newWidth);

          // Compare normalized dimensions to determine if new size is smaller
          if (
            normalizedNewHeight < normalizedOldHeight ||
            normalizedNewWidth < normalizedOldWidth
          ) {
            onIndexOfPointerOut(index);
          } else if (indexOfPointerOut > -1) {
            onIndexOfPointerOut(-1);
          }

          return result;
        });
      } else {
        clearAssetData();
      }
    } catch (error) {
      const result = { id, dimensions, error: true };
      if (assetPreviewFlagRef.current) {
        setHoveredAssetData(result);
      } else {
        clearAssetData();
      }
    }
  };

  const selectedLabels = useMemo(() => {
    if (cardMDType === MetadataType.USER_TAGS) {
      return datasetUserTags
        .filter((option) => option.selected)
        .map((option) => option.text);
    } else {
      return datasetLabels
        .filter((option) => option.selected)
        .map((option) => option.text);
    }
  }, [cardMDType, datasetUserTags, datasetLabels]);

  const showExportButton =
    !!(isSelected || isHovering) &&
    showSelectOption &&
    !isVertex &&
    !hoveredAssetData.id;

  const headerCardType: CardTypes = isObjectModel
    ? "OBJECT"
    : enrichDatasetId && isSingleEntityOnDataPage
      ? singleData?.type.slice(0, singleData?.type?.length - 1)
      : singleData?.type;

  const isObject = [CardTypes.OBJECT, CardTypes.OBJECTS].includes(
    headerCardType
  ); //different from isObjectCard - to be replaced in further refactors
  const selectedMdIcon = getMDIcon(cardMDType);
  const mdOptions = getMdOptions(isObject);

  const debugInfo = useMemo(() => {
    return getDebugInfoText(
      clusterID,
      clusters,
      showDebugInfo,
      singleData,
      isClusterPage
    );
  }, [clusterID, clusters, showDebugInfo, singleData, isClusterPage]);

  const trackEntityOpenAction = (
    target: EventTarget,
    type: "Dataset" | "Cluster Details",
    clusterSize?: number
  ) => {
    let properties: any = {
      [AMP_DATASET_EXPLORATION_PROPERTY__TARGET]: target,
      [AMP_DATASET_EXPLORATION_PROPERTY__CONTEXT]: type,
    };
    if (clusterSize) {
      properties = {
        ...properties,
        [AMP_DATASET_EXPLORATION_PROPERTY__CLUSTER_SIZE]: clusterSize,
      };
    }

    amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__ENTITY__OPENED, properties);
  };

  const handleViewClick = (type: MetadataType) => {
    setCardMDType(type);
    //eslint-disable-next-line
  };
  const handleViewAllClick = (type: MetadataType) => {
    onGlobalMDTypeChange(type);
    setCardMDType(type);
    //eslint-disable-next-line
  };

  const handleLinkClick = () => {
    if (linkClicked) return;
    else setLinkClicked(true);

    let newPath = location.pathname;
    let clusterSize = 0;
    let target: EventTarget = "Image";
    if (isDataPage) {
      const imageCount = singleData?.previews?.length;
      if (imageCount === 1) {
        const firstPreview = singleData.previews[0];
        const isTypeImage = firstPreview.type === "IMAGE";
        const dynamicID = isTypeImage
          ? firstPreview.media_id
          : firstPreview.image_id;

        newPath += `/image/${dynamicID}${entityParam}`;

        if (!isTypeImage) {
          newPath = addQueryParameter(
            newPath,
            OBJECT_ID_QUERY_PARAMETER,
            firstPreview.media_id
          );
        }

        //track url copy action on single entity
        const isVideo = !!firstPreview.video_uri;
        if (isVideo) target = "Video";
        else if (isTypeImage) target = "Image";
        else if (firstPreview.type === "OBJECT") target = "Object";
      } else {
        clusterSize = imageCount;

        const tempParams = new URLSearchParams(searchParams.toString());
        tempParams.set(DATA_PAGE_PARAMETER, tempParams.get(PAGE_PARAMETER)!);
        tempParams.delete(PAGE_PARAMETER);
        const params = `?${tempParams.toString()}`;

        newPath += `/cluster/${clusterID}${params}`;
        target = "Cluster";
      }
    } else if (isClusterPage) {
      const dynamicID =
        singleData.type === "IMAGE" ? singleData.media_id : singleData.image_id;

      let imagePath =
        location.pathname.split("/cluster")[0] +
        `/image/${dynamicID}${entityParam}`;

      if (isSingleObject) {
        imagePath = addQueryParameter(
          imagePath,
          OBJECT_ID_QUERY_PARAMETER,
          singleData.media_id
        );
      }

      newPath = imagePath;

      //track url copy action on single entity
      const isVideo = !!singleData.video_uri;
      if (isVideo) target = "Video";
      else if (singleData.type === "IMAGE") target = "Image";
      else if (singleData.type === "OBJECT") target = "Object";
    }
    // track link copy action
    let properties: any = {
      [AMP_DATASET_EXPLORATION_PROPERTY__TARGET]: target,
    };
    if (!!clusterSize) {
      properties = {
        ...properties,
        [AMP_DATASET_EXPLORATION_PROPERTY__CLUSTER_SIZE]: clusterSize,
      };
    }
    amplitudeTrack(
      AMP_DATASET_EXPLORATION_EVENT__ENTITY__URL_COPIED,
      properties
    );

    const url = window.location.host + newPath;
    copyToClipboard(url.toString());
  };

  const handleDetailsButtonClick = (e: any, data: Cluster) => {
    if (data.onDetailsClick) {
      data.onDetailsClick(data);
      return;
    }

    if (e.target?.classList?.toString().includes("noDetailsClick")) {
      return;
    }

    if (isVertex) {
      return;
    }

    e.stopPropagation();
    clearAssetData();
    dispatch(clearImagePageParamsContext());

    if (isDataPage) {
      dispatch(setSimilarityData(null));
      dispatch(setNavigationCluster(null));

      const imageCount = data?.previews?.length;
      if (imageCount === 1) {
        const firstPreview = data.previews[0];
        const isTypeImage = firstPreview.type === "IMAGE";
        const dynamicID = isTypeImage
          ? firstPreview.media_id
          : firstPreview.image_id;

        dispatch(setNavigationCluster(data));
        let basePath = `${location.pathname}/image/${dynamicID}${entityParam}`;

        dispatch(setDataPageScrollPosition(index));
        if (isTypeImage) {
          navigate(basePath);
        } else {
          const newPath = addQueryParameter(
            basePath,
            OBJECT_ID_QUERY_PARAMETER,
            firstPreview.media_id
          );
          navigate(newPath);
        }

        //track action when single entity on dataset exploration page
        const isVideo = !!firstPreview.video_uri;
        let target: EventTarget = "Image";
        if (isVideo) {
          target = "Video";
        } else if (isTypeImage) {
          target = "Image";
        } else if (firstPreview.type === "OBJECT") {
          target = "Object";
        }
        trackEntityOpenAction(target, "Dataset");
      } else {
        searchParams.set(
          DATA_PAGE_PARAMETER,
          searchParams.get(PAGE_PARAMETER) || ""
        );

        dispatch(
          fetchClusterPageIssueTypes(
            datasetId,
            clusterID,
            queryParamsFilters
          ) as any
        );

        searchParams.delete(PAGE_PARAMETER);
        const newPath = `${location.pathname}/cluster/${clusterID}`;
        const params = `?${searchParams.toString()}`;
        dispatch(setDataPageScrollPosition(index));
        navigate(newPath.concat(params));

        const clusterSize =
          singleData.type === "IMAGES"
            ? singleData.n_images
            : singleData.n_objects;
        trackEntityOpenAction("Cluster", "Dataset", clusterSize);
      }
    }

    if (isClusterPage) {
      const dynamicID =
        singleData.type === "IMAGE" ? singleData.media_id : singleData.image_id;

      let imagePath =
        location.pathname.split("/cluster")[0] +
        `/image/${dynamicID}${entityParam}`;

      if (isSingleObject) {
        imagePath = addQueryParameter(
          imagePath,
          OBJECT_ID_QUERY_PARAMETER,
          singleData.media_id
        );
      }

      dispatch(setSelectedCarouselIndex(index));
      dispatch(setClusterPageScrollPosition(index));
      navigate(imagePath);

      //track action when single entity on cluster details page
      const isVideo = !!singleData.video_uri;
      let target: EventTarget = "Image";

      if (isVideo) {
        target = "Video";
      } else if (singleData.type === "Image") {
        target = "Image";
      } else if (singleData.type === "OBJECT") {
        target = "Object";
      }
      trackEntityOpenAction(target, "Cluster Details");
    }
  };

  const handleSimilarButtonClick = () => {
    clearAssetData();
    if (onSetVertex) {
      if (isDataPage) {
        onSetVertex(clusterID, "CLUSTER");
      } else {
        onSetVertex(singleData.media_id, "MEDIA");
      }
    }
    dispatch(clearImagePageParamsContext());
    trackVisualSearch();
  };

  const handleAssetPreviewSetVertex = () => {
    clearAssetData();
    if (onSetVertex) {
      if (isSingleEntityOnDataPage) {
        onSetVertex(clusterID, "CLUSTER");
      } else {
        onSetVertex(hoveredAssetData.id, "MEDIA");
      }
    }
    dispatch(clearImagePageParamsContext());
    trackVisualSearch();
  };

  const trackVisualSearch = () => {
    let target: EventTarget = "Image";
    let clusterSize: number = 0;
    const isSingleEntity =
      singleData.previews && singleData.previews.length === 1;

    if (isSingleEntity) {
      const singlePreview = singleData.previews[0];
      const isVideo = !!singlePreview.video_uri;
      if (isVideo) target = "Video";
      else if (singlePreview.type === "IMAGE") target = "Image";
      else if (singlePreview.type === "OBJECT") target = "Object";
    } else if (singleData.type === "IMAGES") {
      target = "Cluster";
      clusterSize = singleData.n_images;
    } else if (singleData.type === "OBJECTS") {
      target = "Cluster";
      clusterSize = singleData.n_objects;
    }
    let properties: any = {
      [AMP_DATASET_EXPLORATION_PROPERTY__TARGET]: target,
    };
    if (!!clusterSize) {
      properties = {
        ...properties,
        [AMP_DATASET_EXPLORATION_PROPERTY__CLUSTER_SIZE]: clusterSize,
      };
    }
    //track visual search in amplitude(applied here)
    amplitudeTrack(
      AMP_DATASET_EXPLORATION_EVENT__VISUAL_SEARCH__APPLIED,
      properties
    );
  };

  const handleMouseEnter = (e: any) => {
    e.stopPropagation();
    setIsHovering(true);
    setAssetPreviewFlag(true);
    if (indexOfPointerOut === index) {
      onIndexOfPointerOut(-1);
    }
  };
  const handleMouseLeave = (e: any) => {
    e.stopPropagation();
    setIsHovering(false);
    setLinkClicked(false);
    setAssetPreviewFlag(false);
  };

  const handleMDLabelSelection = useCallback(
    (value: string) => {
      const updateLabels = (
        labels: FilterOption[],
        setLabelsAction: any,
        param: string,
        field: keyof FilterOption = "text"
      ) => {
        const selectedLabel = labels.find((option) => option.text === value);

        if (selectedLabel) {
          const newLabels = [
            ...labels.filter((option) => option.text !== value),
            { ...selectedLabel, selected: !selectedLabel.selected },
          ];
          if (isNewFiltersPanel) {
            if (param === LABELS_PARAMETER) {
              if (selectedLabel.selected) {
                const labelQuery = vqlQueries.find(
                  (query): query is VqlQueryBase & LabelsFilter =>
                    isVqlQuery(query, "labels") &&
                    query.labels.op === "is" &&
                    query.labels.value.includes(value)
                );
                if (labelQuery) {
                  // Remove the value from the query
                  const updatedValues = labelQuery.labels.value.filter(
                    (label: string) => label !== value
                  );

                  if (updatedValues.length > 0) {
                    // Update the query with the remaining values
                    updateVqlQuery({
                      ...labelQuery,
                      labels: {
                        ...labelQuery.labels,
                        value: updatedValues,
                      },
                    });
                  } else {
                    // If no values left, remove the query entirely
                    removeVqlQuery(labelQuery.id);
                  }
                }
              } else {
                addVqlQuery(
                  createAndUpdateLabelsFilter([value], QueryOperator.IS)
                );
              }
            } else if (param === USER_TAGS_PARAMETER) {
              if (selectedLabel.selected) {
                const tagsQuery = vqlQueries.find(
                  (query): query is VqlQueryBase & TagsFilter =>
                    isVqlQuery(query, "tags") &&
                    query.tags.op === "is" &&
                    query.tags.value.includes(selectedLabel.id.toString())
                );
                if (tagsQuery) {
                  // Remove the value from the query
                  const updatedValues = tagsQuery.tags.value.filter(
                    (tag: string) => tag !== selectedLabel.id.toString()
                  );

                  if (updatedValues.length > 0) {
                    // Update the query with the remaining values
                    updateVqlQuery({
                      ...tagsQuery,
                      tags: {
                        ...tagsQuery.tags,
                        value: updatedValues,
                      },
                    });
                  } else {
                    // If no values left, remove the query entirely
                    removeVqlQuery(tagsQuery.id);
                  }
                }
              } else {
                addVqlQuery(
                  createAndUpdateUserTagsFilter(
                    [selectedLabel.id.toString()],
                    QueryOperator.IS
                  )
                );
              }
            }
          } else {
            const result = newLabels
              .filter((option) => option.selected)
              .map((option) => option[field] as string);
            if (result.length > 0) {
              searchParams.set(param, JSON.stringify(result));
            } else {
              searchParams.delete(param);
            }

            searchParams.set(PAGE_PARAMETER, "1");
            navigate({ search: `?${searchParams.toString()}` });
          }

          if (setLabelsAction) {
            dispatch(setLabelsAction(newLabels) as any);
          }
        }
      };

      switch (cardMDType) {
        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.IMAGE_CAPTIONS:
          return;
        case MetadataType.USER_TAGS:
          updateLabels(datasetUserTags, setUserTags, USER_TAGS_PARAMETER, "id");
          break;
        case MetadataType.IMAGE_LABELS:
        case MetadataType.OBJECT_LABELS:
          updateLabels(datasetLabels, undefined, LABELS_PARAMETER);
          break;
      }

      onGlobalMDTypeChange(cardMDType);
    },
    //eslint-disable-next-line
    [cardMDType, searchParams]
  );

  const renderActionButtons = () => {
    return (
      <div className={styles.actionButtons}>
        <div
          onClick={handleLinkClick}
          data-tooltip-id={"data-page-link-button-tooltip"}
          data-tooltip-content={linkClicked ? "Copied!" : "Copy URL"}
          className={classNames(styles.actionButton, styles.noDetailsClick)}
        >
          <LinkIcon clicked={linkClicked} />

          <TooltipWrapper
            id="data-page-link-button-tooltip"
            customClass="cardActionButton"
          />
        </div>
        {!isVertex && (
          <div
            onClick={handleSimilarButtonClick}
            data-tooltip-content="Find Similar"
            data-tooltip-id="data-page-find-similar-button-tooltip"
            className={classNames(styles.actionButton, styles.noDetailsClick)}
          >
            <FindSimilarIcon />
            <TooltipWrapper
              id="data-page-find-similar-button-tooltip"
              customClass="cardActionButton"
            />
          </div>
        )}
      </div>
    );
  };

  const renderCardSelectionButtons = () => {
    if (searchParams.has(REVIEW_PARAMETER)) return <noscript />;

    const wrapperClassNames = classNames(
      styles.noDetailsClick,
      styles.removeSimilarityBtn,
      styles.selectionButtonWrapper,
      { [styles.selectedWrapper]: isSelected || isVertex }
    );

    if (isVertex) {
      return (
        <div onClick={onRemoveVertex} className={wrapperClassNames}>
          <div
            data-testid="remove-selection-button"
            className={styles.selectionButton}
          >
            <XIcon variant="new" />
          </div>
        </div>
      );
    } else if (showExportButton) {
      return (
        <div
          className={wrapperClassNames}
          onClick={(e) =>
            onSelect(e, isSelected, specificIndex, clusterID, singleData)
          }
        >
          <div
            className={styles.selectionButton}
            data-testid="export-selection-button"
          >
            {isSelected && (
              <div
                data-testid="export-selected-button"
                className={styles.selection}
              >
                <CheckIcon />
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return <noscript />;
    }
  };

  const renderAssetPreview = () => {
    const assetData = getAssetData();
    const showCarousel =
      singleData.previews &&
      singleData.previews.length > 1 &&
      hoveredAssetData.id &&
      !hoveredAssetData.error;

    const enrichmentBoundingBoxes = isObjectOnEnrichmentPage
      ? getEnrichmentBoundingBoxes()
      : assetData.bounding_box
        ? [assetData.bounding_box]
        : undefined;

    return (
      <AssetPreviewCard
        uniqueClusterWrapperId={uniqueClusterWrapperId}
        indexOfPointerOut={indexOfPointerOut}
        hoveredAssetData={hoveredAssetData}
        selectedLabels={selectedLabels}
        selectedMdIcon={selectedMdIcon}
        showCarousel={showCarousel}
        containerRef={containerRef}
        isSelected={isSelected}
        singleData={singleData}
        assetData={assetData}
        debugInfo={debugInfo}
        titleIcon={titleIcon}
        mdOptions={mdOptions}
        isVertex={isVertex}
        title={finalTitle}
        index={index}
        key={index}
        clearAssetData={clearAssetData}
        onUpdateAssetData={updateAssetData}
        onSetAssetPreviewFlag={setAssetPreviewFlag}
        onSetAssetPreviewVertex={handleAssetPreviewSetVertex}
        //enrichment data
        mdLabels={mdLabels}
        cardMDType={cardMDType}
        enrichmentBoundingBoxes={enrichmentBoundingBoxes}
        onViewClick={handleViewClick}
        onViewAllClick={handleViewAllClick}
        onMDLabelSelection={handleMDLabelSelection}
      />
    );
  };

  useEffect(() => {
    const type = getInitialMDType(
      globalMDType,
      userTags,
      labels,
      captions,
      entityParams,
      hideMetadataOptions
    );
    if (type !== cardMDType) {
      setCardMDType(type);
    }
    //eslint-disable-next-line
  }, [globalMDType]);

  useEffect(() => {
    let result: string[] = [];

    // Set labels based on isEnrichmentPage
    if (isEnrichmentPage) {
      result = getEnrichPageLabels();
    } else if (isDataCreationPage) {
      result = getCreationPageLabels();
    } else {
      switch (cardMDType) {
        case MetadataType.IMAGE_LABELS:
        case MetadataType.OBJECT_LABELS:
          result = labels;
          break;
        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.IMAGE_CAPTIONS:
          result = captions;
          break;
        case MetadataType.USER_TAGS:
          result = userTags;
          break;
      }
    }

    setMdLabels(result);
    //eslint-disable-next-line
  }, [
    width,
    showMetadataSummary,
    isEnrichmentPage,
    isDataCreationPage,
    globalMDType,
    cardMDType,
    userTagsData,
    hoveredAssetData,
  ]);

  const [isPopoverOpen, setIsPopOverOpen] = useState<boolean>();

  return (
    <div
      ref={cardElementRef}
      id={uniqueClusterWrapperId}
      key={uniqueClusterWrapperId}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={classNames(styles.singleClusterWrapper, {
        [styles[singleData.borderColor]]: singleData.borderColor,
        [styles.singleClusterWrapperIgnored]: singleData.displayIgnorLayout,
      })}
      onClick={(e: any) => {
        if (!hideMetadataOptions) {
          handleDetailsButtonClick(e, singleData);
        }
      }}
    >
      <div
        className={classNames(styles.extendedArea, styles.noDetailsClick)}
        onMouseEnter={() => setIsHovering(true)}
      ></div>
      <div className={cardClassNames}>
        <CardHeader
          index={index}
          title={finalTitle}
          mdLabels={mdLabels}
          cardVariant={cardVariant}
          titleIcon={titleIcon}
          debugInfo={debugInfo}
          mdOptions={mdOptions}
          cardMdType={cardMDType}
          selectedLabels={selectedLabels}
          selectedMdIcon={selectedMdIcon}
          onViewClick={handleViewClick}
          onViewAllClick={handleViewAllClick}
          onMDLabelSelection={
            isBodyUnclickable ? undefined : handleMDLabelSelection
          }
          reviewTitle={inReview ? singleData?.label : null}
        />
        {singleData.floatingDetails && (
          <div className={styles.floatingDetails}>
            {singleData.floatingDetails}
          </div>
        )}

        {singleData.actionList && isHovering && (
          <div className={styles.reviewActionWrapper}>
            <div className={styles.reviewActionListInner}>
              <FloatButton.Group
                type="primary"
                shape="square"
                className={styles.actionList}
              >
                {singleData?.actionList?.map(
                  (item: ActionItem, actionIndex: number) => (
                    <div key={actionIndex} className={styles.actionItem}>
                      <VLPopover
                        placement={"bottom"}
                        trigger={"click"}
                        open={isPopoverOpen}
                        content={
                          item.subAction ? (
                            <SelectList
                              open={true}
                              options={item.subAction.options}
                              onChange={(e) => {
                                item?.subAction?.onChange &&
                                  item.subAction.onChange(e);
                                setIsPopOverOpen(false);
                              }}
                            />
                          ) : undefined
                        }
                      >
                        <VLFloatButton
                          icon={item.action.icon}
                          onClick={item.action.onClick}
                          color={item.action.color}
                          tooltip={item.tooltip}
                        />
                      </VLPopover>
                    </div>
                  )
                )}
              </FloatButton.Group>
            </div>
          </div>
        )}

        <CardBody
          opacity={singleData.opacity}
          isHovering={isHovering}
          plainImage={plainImage}
          thumbImage={thumbImage}
          hoverDelay={hoverDelay}
          mdLabels={mdLabels || []}
          plainImages={plainImages}
          thumbImages={thumbImages}
          boundingBox={boundingBox}
          videoURIs={videoURIsForData}
          bodyEntityType={bodyEntityType}
          unClickable={isBodyUnclickable}
          isSingleObject={isSingleObject}
          videoURIForCluster={videoURIForCluster}
          isAssetPreviewEnabled={dynamicAssetPreviewEnabled}
          onUpdateAssetData={updateAssetData}
          onEndContentLoading={endContentLoading}
        />

        {renderCardSelectionButtons()}
        {showActionButtons && !inReview && renderActionButtons()}
      </div>
      {dynamicAssetPreviewEnabled && renderAssetPreview()}
    </div>
  );
};

export default EnrichmentCard;
